import { Target, CheckCircle, Clock } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import type { CompetencyStatusItem } from '@/types';
import { useCategoryLabel } from '@/api/categories';

export function CompetencyMatrixSection({ statuses }: { statuses?: CompetencyStatusItem[] }) {
  // Categories are configurable (system_categories) — resolve labels dynamically so
  // admin-created competency categories never render as raw slugs (UX-COPY-01).
  const categoryLabel = useCategoryLabel('competency_category');
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Mis Competencias</h2>
      </div>
      {!statuses?.length ? (
        <EmptyState
          title="Sin competencias por seguir"
          description="Tu puesto aún no tiene competencias ligadas a actividades."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {statuses.map((s) => {
            const met = s.status === 'met';
            const expired = s.status === 'expired';
            return (
              <div
                key={s.id}
                className={cn(
                  'rounded-xl border bg-card p-4',
                  met ? 'border-green-300' : expired ? 'border-gray-300' : 'border-amber-300',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-card-foreground">
                      {s.position_competency.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {categoryLabel(s.position_competency.category)} · {s.position.name}
                    </p>
                  </div>
                  {met ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      <CheckCircle className="h-3.5 w-3.5" /> Cumplida
                    </span>
                  ) : expired ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      <Clock className="h-3.5 w-3.5" /> Vencida
                    </span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                      <Clock className="h-3.5 w-3.5" /> En progreso
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {met && s.met_at
                    ? `Cumplida el ${formatDate(s.met_at)}`
                    : `${s.evidence?.length ?? 0} actividad(es) completada(s)`}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
